import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/auth/authStore';
import { ErrorBox, Loading, PageTitle } from '@/components/ui';

interface AudioItem {
  key: string;
  label: string;
  category: 'ui' | 'sfx' | 'match' | 'bgm';
  loop: boolean;
  defaultVolume: number;
  url: string | null;
  volume: number;
  enabled: boolean;
}

const BASE = '';

async function req<T>(method: string, path: string, body?: unknown, isForm = false): Promise<T> {
  const token = useAuthStore.getState().token;
  const headers: Record<string, string> = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  if (!isForm) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: isForm ? (body as FormData) : body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let detail = '';
    try { const j = await res.json(); detail = j?.error ?? j?.message ?? ''; } catch { /* */ }
    throw new Error(detail || `HTTP ${res.status}`);
  }
  return res.json();
}

export function AudioPage() {
  const [items, setItems] = useState<AudioItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = () =>
    req<{ items: AudioItem[] }>('GET', '/admin/audio/')
      .then(({ items }) => setItems(items))
      .catch((e) => setErr(String(e)));

  useEffect(() => { void load(); }, []);

  if (err) return <ErrorBox text={err} />;
  if (!items) return <Loading />;

  const byCat = items.reduce<Record<string, AudioItem[]>>((acc, it) => {
    (acc[it.category] ??= []).push(it);
    return acc;
  }, {});

  return (
    <div>
      <PageTitle title="Audio Assets" />
      <div
        className="card"
        style={{
          padding: '10px 14px',
          marginBottom: 16,
          fontSize: 12,
          color: 'var(--ink-muted)',
          background: 'rgba(34,211,238,0.08)',
          borderColor: 'rgba(34,211,238,0.25)',
        }}
      >
        Upload MP3 / OGG / WAV / M4A files for each event slot (max 20MB).
        SFX/UI/match slots fall back to a synthesized cue when empty, but the
        BGM slots have no synth fallback — upload a looping track there to give
        the app, lobby, matchmaking and match screens background music.
        Volume slider scales the slot mix (0–1); user's SFX/Music slider
        further attenuates on top.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {(['ui', 'match', 'bgm', 'sfx'] as const).map((cat) => {
          const list = byCat[cat];
          if (!list || list.length === 0) return null;
          return (
            <div key={cat} className="card" style={{ padding: 16 }}>
              <div className="tag" style={{ color: 'var(--c-cyan)', marginBottom: 12 }}>
                {cat.toUpperCase()}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {list.map((it, idx) => (
                  <Row
                    key={it.key}
                    item={it}
                    first={idx === 0}
                    busy={busy === it.key}
                    onBusy={(b) => setBusy(b ? it.key : null)}
                    onChanged={load}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Row({
  item, first, busy, onBusy, onChanged,
}: {
  item: AudioItem;
  first: boolean;
  busy: boolean;
  onBusy: (b: boolean) => void;
  onChanged: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [vol, setVol] = useState(item.volume);
  const [enabled, setEnabled] = useState(item.enabled);

  async function upload(file: File) {
    onBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await req(`POST`, `/admin/audio/${encodeURIComponent(item.key)}/upload`, form, true);
      await onChanged();
    } catch (e: any) {
      alert(`Upload failed: ${e?.message ?? e}`);
    }
    onBusy(false);
  }
  async function clearFile() {
    if (!confirm('Remove the uploaded file (revert to synth)?')) return;
    onBusy(true);
    try {
      await req('DELETE', `/admin/audio/${encodeURIComponent(item.key)}/file`);
      await onChanged();
    } catch (e: any) {
      alert(`Failed: ${e?.message ?? e}`);
    }
    onBusy(false);
  }
  async function saveMeta() {
    onBusy(true);
    try {
      await req('PATCH', `/admin/audio/${encodeURIComponent(item.key)}`, { volume: vol, enabled });
      await onChanged();
    } catch (e: any) {
      alert(`Save failed: ${e?.message ?? e}`);
    }
    onBusy(false);
  }
  function preview() {
    if (audioRef.current && item.url) {
      audioRef.current.currentTime = 0;
      audioRef.current.volume = vol;
      void audioRef.current.play();
    }
  }

  return (
    <div
      style={{
        borderTop: first ? 'none' : '1px solid var(--hairline)',
        paddingTop: first ? 0 : 12,
        paddingBottom: 12,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <span style={{ fontSize: 13 }}>{item.label}</span>
        <span className="mono" style={{ fontSize: 10, color: 'var(--ink-dim)' }}>
          {item.key}{item.loop ? ' · loop' : ''}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {item.url ? (
          <>
            <span style={{ fontSize: 11, color: 'var(--c-cyan)' }}>✓ uploaded</span>
            <button className="btn" onClick={preview} disabled={busy} style={{ fontSize: 11, padding: '4px 10px' }}>
              ▶ PLAY
            </button>
            <button className="btn btn-ghost" onClick={clearFile} disabled={busy} style={{ fontSize: 11, padding: '4px 10px', color: 'var(--c-red)' }}>
              REMOVE
            </button>
            <audio ref={audioRef} src={item.url} preload="auto" />
          </>
        ) : (
          <span style={{ fontSize: 11, color: 'var(--ink-dim)' }}>(no file — using synth fallback)</span>
        )}
        <input
          type="file"
          accept="audio/*"
          ref={fileRef}
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void upload(f);
            e.target.value = '';
          }}
        />
        <button className="btn btn-primary" onClick={() => fileRef.current?.click()} disabled={busy} style={{ fontSize: 11, padding: '4px 10px' }}>
          {item.url ? 'REPLACE FILE' : 'UPLOAD FILE'}
        </button>
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 10 }}>
        <label style={{ fontSize: 11, color: 'var(--ink-muted)', minWidth: 60 }}>Volume</label>
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={vol}
          onChange={(e) => setVol(parseFloat(e.target.value))}
          style={{ flex: 1 }}
        />
        <span className="mono" style={{ fontSize: 11, color: 'var(--c-yellow)', minWidth: 40 }}>
          {Math.round(vol * 100)}%
        </span>
        <label style={{ fontSize: 11, color: 'var(--ink-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          on
        </label>
        <button className="btn" onClick={saveMeta} disabled={busy || (vol === item.volume && enabled === item.enabled)} style={{ fontSize: 11, padding: '4px 10px' }}>
          SAVE
        </button>
      </div>
    </div>
  );
}
